import { createApi } from '@reduxjs/toolkit/dist/query/react';
import type { BaseQueryFn } from '@reduxjs/toolkit/dist/query';
import { axiosBaseQuery } from './AxiosBaseQuery';
import { API_SERVER_URL } from '@/constants/Config';

export interface IParticipantShippingPreviewRequest {
  orderId: string;
  participantId: string;
  street_1?: string;
  street_2?: string;
  city?: string;
  state?: string;
  zip_code?: string;
  attention_name?: string;
  shipping_name?: string;
  country?: string;
}

export interface IParticipantShippingPreviewResponse {
  shippingCharge: number;
  salesTax: number;
  newTotal: number;
  differenceAmount: number;
  previousShippingCharge: number;
  previousSalesTax: number;
  previousTotal: number;
}

export interface IParticipantOrderItemUpdatePayload {
  participant_order_item_id: number;
  primary_order_item_id: number;
  participant_size: string;
}

export interface IUpdateParticipantOrderItemsRequest {
  orderId: string;
  participantId: string;
  orderItems: IParticipantOrderItemUpdatePayload[];
}

export interface IUpdateParticipantShippingAddressRequest {
  orderId: string;
  participantId: string;
  shipping_name?: string;
  attention_name?: string;
  street_1?: string;
  street_2?: string;
  city?: string;
  state?: string;
  zip_code?: string;
  country?: string;
  shippingCharge: number;
  salesTax: number;
  newTotal: number;
  differenceAmount: number;
}

export interface IBulkShippingPreviewRequest {
  orderId: string;
  street_1?: string;
  street_2?: string;
  city?: string;
  state?: string;
  zip_code?: string;
  attention_name?: string;
  shipping_name?: string;
  country?: string;
}

export interface IBulkShippingPreviewResponse {
  /** Bulk shipping is always free, so this is always 0. */
  shippingCharge: number;
  salesTax: number;
  newTotal: number;
  /** Sales tax delta across all participants (shipping never changes for bulk). */
  differenceAmount: number;
  previousShippingCharge: number;
  previousSalesTax: number;
  previousTotal: number;
  participantsCount: number;
}

export interface IUpdateBulkShippingAddressRequest {
  orderId: string;
  shipping_name?: string;
  attention_name?: string;
  street_1?: string;
  street_2?: string;
  city?: string;
  state?: string;
  zip_code?: string;
  country?: string;
  salesTax: number;
  newTotal: number;
  differenceAmount: number;
}

export interface IUpdateBulkShippingAddressResponse {
  participantsUpdated: number;
  participantSalesTaxById: Record<string, number>;
}

const rawParticipantEditBaseQuery = axiosBaseQuery({
  baseUrl: API_SERVER_URL,
});

const hasShippingPreviewFields = (payload: Record<string, unknown>): boolean =>
  'salesTax' in payload ||
  'sales_tax' in payload ||
  'differenceAmount' in payload ||
  'difference_amount' in payload ||
  'shippingCharge' in payload ||
  'shipping_charge' in payload ||
  'newTotal' in payload ||
  'new_total' in payload ||
  'participantsCount' in payload ||
  'participants_count' in payload;

/**
 * `axiosBaseQuery` returns the axios JSON body directly, but RTK Query requires
 * `{ data }` / `{ error }`.
 *
 * Critical: never peel a nested `data` field when pricing fields already exist on
 * the root — some responses include both and peeling discards `salesTax` (UI stays $0).
 */
const participantEditBaseQuery: BaseQueryFn<
  {
    url: string;
    method: string | undefined;
    data?: unknown;
    params?: unknown;
  },
  unknown,
  unknown
> = async (args, api, extraOptions) => {
  const result = await rawParticipantEditBaseQuery(args, api, extraOptions);

  if (result && typeof result === 'object' && 'error' in result) {
    return result;
  }

  if (!result || typeof result !== 'object') {
    return { data: result };
  }

  const body = result as Record<string, unknown>;
  const bodyKeys = Object.keys(body);

  // Already a valid RTK Query result: `{ data }` (optional `meta`).
  if (
    'data' in body &&
    !('error' in body) &&
    bodyKeys.every((key) => key === 'data' || key === 'meta')
  ) {
    return body as { data: unknown };
  }

  // Prefer root payload when it carries shipping-preview pricing fields.
  if (hasShippingPreviewFields(body)) {
    return { data: body };
  }

  // Common API envelope: `{ status, data: payload, message? }`.
  if (body.data && typeof body.data === 'object' && !Array.isArray(body.data)) {
    return { data: body.data };
  }

  // Flat non-preview payloads (e.g. `{ participantsUpdated: 1 }`).
  return { data: result };
};

export const participantEditQuery = createApi({
  reducerPath: 'participantEditQuery',
  baseQuery: participantEditBaseQuery,
  endpoints: (build) => ({
    previewParticipantShipping: build.mutation<
      IParticipantShippingPreviewResponse,
      IParticipantShippingPreviewRequest
    >({
      query: ({ orderId, participantId, ...body }) => ({
        url: `api/store/${orderId}/participants/${participantId}/shipping-preview`,
        method: 'POST',
        data: body,
      }),
    }),
    updateParticipantOrderItems: build.mutation<
      { participant: Record<string, unknown> },
      IUpdateParticipantOrderItemsRequest
    >({
      query: ({ orderId, participantId, orderItems }) => ({
        url: `api/store/${orderId}/participants/${participantId}/order-items`,
        method: 'PUT',
        data: { orderItems },
      }),
    }),
    updateParticipantShippingAddress: build.mutation<
      { participant: Record<string, unknown> },
      IUpdateParticipantShippingAddressRequest
    >({
      query: ({ orderId, participantId, ...body }) => ({
        url: `api/store/${orderId}/participants/${participantId}/shipping-address`,
        method: 'PUT',
        data: body,
      }),
    }),
    previewBulkShipping: build.mutation<IBulkShippingPreviewResponse, IBulkShippingPreviewRequest>({
      query: ({ orderId, ...body }) => ({
        url: `api/store/${orderId}/bulk-shipping/shipping-preview`,
        method: 'POST',
        data: body,
      }),
    }),
    updateBulkShippingAddress: build.mutation<
      IUpdateBulkShippingAddressResponse,
      IUpdateBulkShippingAddressRequest
    >({
      query: ({ orderId, ...body }) => ({
        url: `api/store/${orderId}/bulk-shipping/shipping-address`,
        method: 'PUT',
        data: body,
      }),
    }),
  }),
});

export const {
  usePreviewParticipantShippingMutation,
  useUpdateParticipantOrderItemsMutation,
  useUpdateParticipantShippingAddressMutation,
  usePreviewBulkShippingMutation,
  useUpdateBulkShippingAddressMutation,
} = participantEditQuery;
